import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  DialogContent,
  Grid,
  ButtonGroup,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { format, getYear } from "date-fns";
import { es } from "date-fns/locale";
import GridViewIcon from "@mui/icons-material/GridView";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import LandscapeIcon from "@mui/icons-material/Landscape";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { useZoneFilter } from "../contexts/ZoneFilterContext";
import AppDialog from "../../shared/components/AppDialog";
import WebSocketStatus from "./WebSocketStatus";

const CalendarHeader = ({
  selectedDate,
  onDateChange,
  onMarcarFueraDeServicio,
}: {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  /** Solo llega para quien puede administrar: sin handler, no hay botón. */
  onMarcarFueraDeServicio?: () => void;
}) => {
  const theme = useTheme();
  const isShortDesktop = useMediaQuery("(min-width:900px) and (max-height:820px)");
  const [open, setOpen] = useState(false);
  const { zoneFilter, setZoneFilter } = useZoneFilter();
  const [selectedYear, setSelectedYear] = useState(getYear(selectedDate));

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    const newYear = Number(event.target.value);
    setSelectedYear(newYear);
    const newDate = new Date(newYear, selectedDate.getMonth(), 1);
    onDateChange(newDate);
  };

  const handleSelectDate = (month: number) => {
    const newDate = new Date(selectedYear, month, 1);
    onDateChange(newDate);
    setOpen(false);
  };

  const handleChangeMonth = (offset: number) => {
    onDateChange(
      new Date(selectedDate.getFullYear(), selectedDate.getMonth() + offset, 1)
    );
  };

  const handleGoToday = () => {
    const today = new Date();
    onDateChange(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const monthLabel = format(selectedDate, "MMMM yyyy", { locale: es });

  useEffect(() => {
    setSelectedYear(getYear(selectedDate));
  }, [selectedDate]);

  const currentYear = getYear(new Date());
  const startYear = 2020;
  const years = Array.from(
    { length: currentYear - startYear + 6 },
    (_, i) => startYear + i
  );

  const btnHeight = isShortDesktop ? 34 : 40;
  const headerPy = isShortDesktop ? 0.5 : 1;
  const gap = isShortDesktop ? 1 : { xs: 1, sm: 2 };
  const monthFontSize = isShortDesktop ? "0.92rem" : { xs: "0.95rem", sm: "1.05rem" };
  const monthMinWidth = isShortDesktop ? 140 : { xs: 136, sm: 170 };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: headerPy,
        backgroundColor: theme.palette.background.paper,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: gap,
          justifyContent: "center",
          flexWrap: "wrap",
          mt: { xs: 1, sm: 0 },
        }}
      >
        <ButtonGroup size="small" variant="outlined">
          <Tooltip title="Todos los departamentos">
            <Button
              onClick={() => setZoneFilter("todos")}
              variant={zoneFilter === "todos" ? "contained" : "outlined"}
              startIcon={<GridViewIcon />}
              sx={{
                "& .MuiButton-startIcon": {
                  color:
                    zoneFilter === "todos"
                      ? "inherit"
                      : theme.palette.secondary.main,
                },
              }}
            >
              Todos
            </Button>
          </Tooltip>
          <Tooltip title="Departamentos del Centro">
            <Button
              onClick={() => setZoneFilter("centro")}
              variant={zoneFilter === "centro" ? "contained" : "outlined"}
              startIcon={<LocationCityIcon />}
              sx={{
                "& .MuiButton-startIcon": {
                  color:
                    zoneFilter === "centro"
                      ? "inherit"
                      : theme.palette.zonas.centro.border,
                },
              }}
            >
              Centro
            </Button>
          </Tooltip>
          <Tooltip title="Departamentos de la Loma">
            <Button
              onClick={() => setZoneFilter("loma")}
              variant={zoneFilter === "loma" ? "contained" : "outlined"}
              startIcon={<LandscapeIcon />}
              sx={{
                "& .MuiButton-startIcon": {
                  color:
                    zoneFilter === "loma"
                      ? "inherit"
                      : theme.palette.zonas.loma.border,
                },
              }}
            >
              Loma
            </Button>
          </Tooltip>
          <Tooltip title="Boedo">
            <Button
              onClick={() => setZoneFilter("interno")}
              variant={zoneFilter === "interno" ? "contained" : "outlined"}
              startIcon={<HomeWorkIcon />}
              sx={{
                "& .MuiButton-startIcon": {
                  color:
                    zoneFilter === "interno"
                      ? "inherit"
                      : theme.palette.zonas.interno.border,
                },
              }}
            >
              Boedo
            </Button>
          </Tooltip>
        </ButtonGroup>

        <WebSocketStatus />

        {onMarcarFueraDeServicio && (
          <>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<EventBusyIcon />}
              onClick={onMarcarFueraDeServicio}
              sx={{
                height: btnHeight,
                display: { xs: "none", sm: "inline-flex" },
                borderColor: "divider",
                color: "text.secondary",
                "&:hover": { borderColor: "text.secondary", bgcolor: "action.hover" },
              }}
            >
              Fuera de servicio
            </Button>
            <Tooltip title="Marcar fuera de servicio">
              <IconButton
                size="small"
                aria-label="Marcar fuera de servicio"
                onClick={onMarcarFueraDeServicio}
                sx={{
                  height: btnHeight,
                  width: btnHeight,
                  display: { xs: "inline-flex", sm: "none" },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  color: "text.secondary",
                }}
              >
                <EventBusyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}

        <Box
          sx={{
            bgcolor: theme.palette.action.hover,
            boxShadow: `inset 0 0 0 1px ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Tooltip title="Mes anterior">
            <IconButton
              size="small"
              onClick={() => handleChangeMonth(-1)}
              sx={{ borderRadius: 0, height: btnHeight, width: 34 }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            onClick={() => setOpen(true)}
            sx={{
              px: { xs: 1.25, sm: 1.5 },
              py: 0.5,
              minWidth: monthMinWidth,
              fontWeight: 700,
              fontSize: monthFontSize,
              textTransform: "capitalize",
              color: "text.primary",
              borderRadius: 0,
              height: btnHeight,
              "&:hover": {
                bgcolor: theme.palette.action.selected,
              },
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {monthLabel}
            <ExpandMoreIcon fontSize="small" />
          </Button>
          <Tooltip title="Mes siguiente">
            <IconButton
              size="small"
              onClick={() => handleChangeMonth(1)}
              sx={{ borderRadius: 0, height: btnHeight, width: 34 }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Volver al mes actual">
            <IconButton
              size="small"
              onClick={handleGoToday}
              sx={{
                borderRadius: 0,
                height: btnHeight,
                width: 36,
                borderLeft: "1px solid",
                borderColor: "divider",
              }}
            >
              <TodayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <AppDialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[3],
            mt: 8,
          },
        }}
      >
        <DialogContent sx={{ p: 3, minHeight: 0, overflowY: "auto", flex: 1 }}>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Año</InputLabel>
              <Select
                value={selectedYear}
                onChange={handleYearChange}
                label="Año"
                sx={{ mb: 2 }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      mt: 1,
                    },
                  },
                }}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Grid container spacing={1}>
            {Array.from({ length: 12 }).map((_, i) => (
              <Grid item xs={4} key={i}>
                <Button
                  fullWidth
                  onClick={() => handleSelectDate(i)}
                  variant={
                    selectedDate.getMonth() === i ? "contained" : "outlined"
                  }
                  sx={{
                    textTransform: "capitalize",
                    py: 1,
                    borderRadius: 2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  {format(new Date(0, i), "MMMM", { locale: es })}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </AppDialog>
    </Box>
  );
};

export default CalendarHeader;
